import { Button, Modal } from 'react-bootstrap';
import { IoClose } from '@react-icons/all-files/io5/IoClose';
import { FINDINGS_GRID_MODAL_HEIGHT, VIEW_IN_RISK_ADVISOR } from 'Core-utils/constants/constants';
import { Typography } from 'Components/atoms';
import { useNavigate } from 'react-router-dom';
import { FINDINGS_ROUTE } from 'Core-utils/routes';
import ModalFindingsGrid from './ModalFindingsGrid';
import './styles.scss';
import { useI18n } from 'Hooks/useI18n';
import {
    extractUserAssociationFromDefaultFilter,
    USER_ASSOCIATION_FILTER_KEY,
} from 'Core-utils/helpers/findingsUserAssociationFilter';
import { useMemo } from 'react';

interface FindingsGridModalProps {
    title: string;
    onClose: () => void;
    show: boolean;
    defaultFilter?: { [key: string]: string[] };
    /** Base API filter criteria (e.g. user_id presence). Not synced to URL set filters. */
    filter?: Record<string, unknown>;
}

/** Coerce modal default filters to string[][] for Ag merge helper. */
function normalizeDefaultFilter(value: FindingsGridModalProps['defaultFilter']): { [key: string]: string[] } {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return {};
    }
    const out: { [key: string]: string[] } = {};
    Object.entries(value).forEach(([k, v]) => {
        if (v === undefined || v === null) {
            return;
        }
        out[k] = Array.isArray(v) ? v : [v];
    });
    return out;
}

const FindingsGridModal = ({ title, onClose, show, defaultFilter, filter }: FindingsGridModalProps) => {
    const { t } = useI18n();
    const navigate = useNavigate();

    const safeDefaultFilter = normalizeDefaultFilter(defaultFilter);
    const { setFilters, userAssociated } = extractUserAssociationFromDefaultFilter(safeDefaultFilter);
    const modalFilterCriteria = useMemo(
        () => ({
            ...(userAssociated ? { [USER_ASSOCIATION_FILTER_KEY]: userAssociated } : {}),
            ...(filter ?? {}),
        }),
        [userAssociated, filter],
    );

    const handleViewClick = () => {
        const searchParams = new URLSearchParams();
        Object.entries(safeDefaultFilter).forEach(([key, values]) => {
            values.forEach((value) => {
                searchParams.append(key, value);
            });
        });

        const search = searchParams.toString();
        navigate(
            {
                pathname: FINDINGS_ROUTE,
                search: search ? `?${search}` : '',
            },
            { state: safeDefaultFilter },
        );
    };

    if (!show) {
        return null;
    }

    return (
        <Modal show={show} className="findingsgrid-modal w-100" centered onHide={onClose}>
            <Modal.Header className="text-text-high-emphasis d-flex justify-content-between">
                <Modal.Title className="h6">{title}</Modal.Title>
                <div className="d-flex gap-3 align-items-center">
                    <Button className="findingsgrid-modal__button" onClick={handleViewClick}>
                        <Typography variant="body1" color="primary-500" style={{ marginBottom: '0%' }}>
                            {t(VIEW_IN_RISK_ADVISOR)}
                        </Typography>
                    </Button>
                    <IoClose
                        className="findingsgrid-modal__icon"
                        size={20}
                        onClick={onClose}
                        data-testid="close-icon"
                    />
                </div>
            </Modal.Header>
            <Modal.Body className="p-0 w-0">
                <ModalFindingsGrid
                    defaultFilter={setFilters}
                    filter={modalFilterCriteria}
                    tableBodyHeight={FINDINGS_GRID_MODAL_HEIGHT}
                />
            </Modal.Body>
        </Modal>
    );
};

export default FindingsGridModal;
